import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, LogOut, FileText, Package, Briefcase, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MieAttivitaPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [attivita, setAttivita] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    fetchAttivita();
  }, []);

  const fetchAttivita = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/mie-attivita`, getAuthHeaders());
      setAttivita(response.data.attivita);
    } catch (error) {
      console.error('Errore nel caricamento attività:', error);
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        onLogout();
      } else {
        toast.error('Errore nel caricamento delle attività');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompletaAttivita = async (e, attivitaItem) => {
    e.stopPropagation(); // Evita navigazione quando si clicca il pulsante
    
    const conferma = window.confirm(`Hai completato l'attività "${attivitaItem.titolo}"?\n\nCliccando OK l'attività verrà rimossa dalla tua lista.`);
    if (!conferma) return;
    
    try {
      setCompletingId(attivitaItem.id);
      await axios.delete(`${API}/mie-attivita/${attivitaItem.tipo}/${attivitaItem.id}`, getAuthHeaders());
      toast.success('Attività completata e rimossa dalla lista');
      // Rimuovi l'attività dalla lista locale
      setAttivita(prev => prev.filter(a => a.id !== attivitaItem.id));
    } catch (error) {
      console.error('Errore nel completamento attività:', error);
      toast.error('Errore nel completamento dell\'attività');
    } finally {
      setCompletingId(null);
    }
  };

  const getTipoIcon = (tipo) => {
    switch(tipo) {
      case 'preventivo': return <FileText className="w-5 h-5" />;
      case 'ordine_lavoro': return <Package className="w-5 h-5" />;
      case 'progetto': return <Briefcase className="w-5 h-5" />;
      default: return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getTipoLabel = (tipo) => {
    switch(tipo) {
      case 'preventivo': return 'Preventivo';
      case 'ordine_lavoro': return 'Ordine di Lavoro';
      case 'progetto': return 'Progetto';
      default: return 'Attività';
    }
  };

  const getTipoBgColor = (tipo) => {
    switch(tipo) {
      case 'preventivo': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'ordine_lavoro': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'progetto': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatoBadgeColor = (stato) => {
    const statiMap = {
      'bozza': 'bg-slate-100 text-slate-700',
      'inviato': 'bg-blue-100 text-blue-700',
      'in attesa': 'bg-amber-100 text-amber-700',
      'accettato': 'bg-green-100 text-green-700',
      'rifiutato': 'bg-red-100 text-red-700',
      'in_corso': 'bg-blue-100 text-blue-700',
      'completato': 'bg-green-100 text-green-700',
      'non_iniziato': 'bg-slate-100 text-slate-700',
      'sospeso': 'bg-orange-100 text-orange-700'
    };
    return statiMap[stato?.toLowerCase()] || 'bg-slate-100 text-slate-700';
  };

  const attivitaPrioritarie = attivita.filter(a => a.priorita === 'alta');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="hover:bg-slate-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Le Mie Attività</h1>
                <p className="text-sm text-slate-600">
                  {attivitaPrioritarie.length > 0 && (
                    <span className="text-red-600 font-medium">
                      {attivitaPrioritarie.length} prioritar{attivitaPrioritarie.length === 1 ? 'ia' : 'ie'}
                    </span>
                  )} {attivita.length} attività totali
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-slate-600">Ciao,</p>
                <p className="font-semibold text-slate-900">{user.nome} {user.cognome}</p>
              </div>
              <Button variant="outline" onClick={onLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
        {loading ? (
          <div className="text-center py-12 text-slate-600">
            Caricamento attività...
          </div>
        ) : attivita.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-md border border-slate-200">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">Nessuna attività assegnata</p>
            <p className="text-sm text-slate-500 mt-1">
              Le tue attività appariranno qui quando ti verranno assegnate
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {attivita.map((attivita, idx) => (
              <div
                key={idx}
                onClick={() => navigate(attivita.link)}
                className={`bg-white rounded-xl shadow-md border-2 p-6 cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-1 ${
                  attivita.priorita === 'alta' ? 'border-red-300 bg-red-50' : 'border-slate-200'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-4 flex-1">
                    {/* Icona Tipo */}
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${getTipoBgColor(attivita.tipo)}`}>
                      {getTipoIcon(attivita.tipo)}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getTipoBgColor(attivita.tipo)}`}>
                          {getTipoLabel(attivita.tipo)}
                        </span>
                        <span className="font-mono font-semibold text-slate-700">
                          {attivita.numero}
                        </span>
                        {attivita.priorita === 'alta' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500 text-white">
                            Priorità Alta
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">
                        {attivita.titolo}
                      </h3>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span>Cliente: {attivita.cliente}</span>
                          {attivita.data && <span>Data: {attivita.data}</span>}
                        </div>
                        {attivita.motivo_priorita && (
                          <div className="flex items-center gap-2 text-sm text-red-600 font-medium">
                            <AlertCircle className="w-4 h-4" />
                            {attivita.motivo_priorita}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stato e Pulsante Completata */}
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-2 rounded-full text-sm font-medium ${getStatoBadgeColor(attivita.stato)}`}>
                        {attivita.stato?.replace('_', ' ').charAt(0).toUpperCase() + attivita.stato?.slice(1).replace('_', ' ')}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => handleCompletaAttivita(e, attivita)}
                        disabled={completingId === attivita.id}
                        className="border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
                        data-testid={`completa-attivita-${attivita.id}`}
                      >
                        {completingId === attivita.id ? (
                          <span className="animate-pulse">...</span>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Completata
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MieAttivitaPage;
